import React, { useState } from "react";
import { FiChevronDown } from "react-icons/fi";

/* =========================================================
   CollapsibleSection
   Wraps any section content in a card with a clickable
   header (icon + title + count) that expands/collapses.
   Each section can have its own accent color.
========================================================= */
export const CollapsibleSection = ({
  icon,
  title,
  count,
  accent = "slate",
  defaultOpen = true,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`vt-section vt-accent-${accent}`}>
      <button
        type="button"
        className="vt-section-header"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <div className="vt-section-title">
          <span className="vt-section-icon">{icon}</span>
          <h3>{title}</h3>
        </div>

        <div className="vt-section-header-right">
          {count !== undefined && (
            <span className="vt-section-count">
              {count} {count === 1 ? "item" : "items"}
            </span>
          )}

          <span className={`vt-chevron ${open ? "vt-chevron-open" : ""}`}>
            <FiChevronDown />
          </span>
        </div>
      </button>

      <div className={`vt-section-body ${open ? "vt-open" : "vt-closed"}`}>
        <div className="vt-section-body-inner">{children}</div>
      </div>
    </div>
  );
};

/* =========================================================
   DetailItem — single label/value pair
========================================================= */
export const DetailItem = ({ label, value }) => (
  <div className="vt-detail-item">
    <span className="vt-detail-label">{label}</span>
    <span className="vt-detail-value">
      {value !== null && value !== undefined && value !== "" ? value : "-"}
    </span>
  </div>
);

/* =========================================================
   StatusBadge — pill used for tenant/document/entity status
========================================================= */
export const getStatusClass = (status) => {
  if (!status) return "vt-status-default";
  const normalized = status.toLowerCase();

  if (["active", "approved", "self_declared"].includes(normalized)) {
    return "vt-status-active";
  }
  if (["pending", "submitted"].includes(normalized)) {
    return "vt-status-pending";
  }
  if (["rejected", "inactive"].includes(normalized)) {
    return "vt-status-rejected";
  }
  return "vt-status-default";
};

export const StatusBadge = ({ status, inline = false }) => (
  <span
    className={`${inline ? "vt-inline-status" : "vt-status-badge"} ${getStatusClass(
      status
    )}`}
  >
    {!inline && <span className="vt-status-dot"></span>}
    {status || "-"}
  </span>
);

/* =========================================================
   LegalEntitiesTable
========================================================= */
export const LegalEntitiesTable = ({ entities, formatDate, formatDateTime }) => {
  if (!entities.length) {
    return <div className="vt-no-records">No legal entities found.</div>;
  }

  return (
    <div className="vt-table-wrapper">
      <table className="vt-data-table">
        <thead>
          <tr>
            <th>Registration Number</th>
            <th>Country</th>
            <th>Incorporation Date</th>
            <th>Status</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {entities.map((entity) => (
            <tr key={entity.id}>
              <td><strong>{entity.registration_number || "-"}</strong></td>
              <td>{entity.country_of_incorporation || "-"}</td>
              <td>{formatDate(entity.incorporation_date)}</td>
              <td><StatusBadge status={entity.status} inline /></td>
              <td>{formatDateTime(entity.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* =========================================================
   TaxRegistrationsTable
========================================================= */
export const TaxRegistrationsTable = ({ taxRegistrations }) => {
  if (!taxRegistrations.length) {
    return <div className="vt-no-records">No tax registrations found.</div>;
  }

  return (
    <div className="vt-table-wrapper">
      <table className="vt-data-table">
        <thead>
          <tr>
            <th>Tax Type</th>
            <th>Country</th>
            <th>Tax Number</th>
            <th>Status</th>
            <th>Legal Entity</th>
          </tr>
        </thead>
        <tbody>
          {taxRegistrations.map((tax) => (
            <tr key={tax.id}>
              <td><strong>{tax.tax_type || "-"}</strong></td>
              <td>{tax.country_code || "-"}</td>
              <td>{tax.tax_number || "-"}</td>
              <td><StatusBadge status={tax.status} inline /></td>
              <td>{tax.legal_entity || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* =========================================================
   LocationsGrid
========================================================= */
export const LocationsGrid = ({ locations }) => {
  if (!locations.length) {
    return <div className="vt-no-records">No locations found.</div>;
  }

  return (
    <div className="vt-location-grid">
      {locations.map((location) => (
        <div className="vt-location-card" key={location.id}>
          <div className="vt-location-header">
            <div>
              <span className="vt-location-type">{location.location_type}</span>
              <h4>{location.location_code || "Location"}</h4>
            </div>
            <StatusBadge
              status={location.is_active ? "ACTIVE" : "INACTIVE"}
              inline
            />
          </div>

          <div className="vt-address">
            <div>
              <p>{location.address_line1 || ""}</p>
              {location.address_line2 && <p>{location.address_line2}</p>}
              <p>
                {location.city}, {location.state}
              </p>
              <p>
                {location.postal_code}, {location.country_code}
              </p>
            </div>
          </div>

          <div className="vt-location-meta">
            <span>
              Head Office: <strong>{location.is_head_office ? "Yes" : "No"}</strong>
            </span>
            <span>
              Default Billing:{" "}
              <strong>{location.is_default_billing ? "Yes" : "No"}</strong>
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

/* =========================================================
   RepresentativesGrid
========================================================= */
export const RepresentativesGrid = ({ representatives, formatDate }) => {
  if (!representatives.length) {
    return <div className="vt-no-records">No authorised representatives found.</div>;
  }

  return (
    <div className="vt-representatives-grid">
      {representatives.map((rep) => (
        <div className="vt-representative-card" key={rep.id}>
          <div className="vt-representative-avatar">
            {rep.full_name?.charAt(0).toUpperCase()}
          </div>

          <div className="vt-representative-content">
            <div className="vt-representative-top">
              <div>
                <h4>{rep.full_name}</h4>
                <span>{rep.title || "-"}</span>
              </div>
              <StatusBadge status={rep.verification_status} inline />
            </div>

            <div className="vt-representative-details">
              <span>{rep.official_email || "-"}</span>
              <span>{rep.mobile || "-"}</span>
              <span>Authority: {rep.authority_type || "-"}</span>
              <span>
                Effective: {formatDate(rep.effective_from)} -{" "}
                {formatDate(rep.effective_to)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/* =========================================================
   DocumentsGrid
========================================================= */
export const DocumentsGrid = ({ documents, formatDate }) => {
  if (!documents.length) {
    return <div className="vt-no-records">No documents found.</div>;
  }

  return (
    <div className="vt-documents-grid">
      {documents.map((document) => (
        <div className="vt-document-card" key={document.id}>
          <div className="vt-document-info">
            <h4>{document.document_type || "Document"}</h4>
            <span>{document.file ? document.file.split("/").pop() : "No file"}</span>

            <div className="vt-document-dates">
              <span>Issue: {formatDate(document.issue_date)}</span>
              <span>Expiry: {formatDate(document.expiry_date)}</span>
            </div>
          </div>

          <StatusBadge status={document.status} inline />
        </div>
      ))}
    </div>
  );
};

/* =========================================================
   LegalAcceptancesList
========================================================= */
export const LegalAcceptancesList = ({ acceptances, formatDateTime }) => {
  if (!acceptances.length) {
    return <div className="vt-no-records">No legal acceptances found.</div>;
  }

  return (
    <div className="vt-acceptance-list">
      {acceptances.map((acceptance) => (
        <div className="vt-acceptance-item" key={acceptance.id}>
          <div className="vt-acceptance-content">
            <h4>{acceptance.acceptance_type}</h4>
            <span>Version {acceptance.version}</span>
          </div>

          <div className="vt-acceptance-meta">
            <span>{acceptance.jurisdiction}</span>
            <span>{formatDateTime(acceptance.created_at)}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

/* =========================================================
   OperationsTable
========================================================= */
export const OperationsTable = ({ operations, formatDate }) => {
  if (!operations.length) {
    return <div className="vt-no-records">No operations found.</div>;
  }

  return (
    <div className="vt-table-wrapper">
      <table className="vt-data-table">
        <thead>
          <tr>
            <th>Country</th>
            <th>Region</th>
            <th>Industry</th>
            <th>Status</th>
            <th>Registration</th>
            <th>Active</th>
            <th>Effective Period</th>
          </tr>
        </thead>
        <tbody>
          {operations.map((operation) => (
            <tr key={operation.id}>
              <td><strong>{operation.country_code || "-"}</strong></td>
              <td>{operation.region_name || "-"}</td>
              <td>{operation.industry || "-"}</td>
              <td><StatusBadge status={operation.status} inline /></td>
              <td>{operation.is_registration_enabled ? "Enabled" : "Disabled"}</td>
              <td>{operation.is_active ? "Yes" : "No"}</td>
              <td>
                {formatDate(operation.effective_from)} -{" "}
                {formatDate(operation.effective_to)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};